import { alpha, Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, MOTION, SURFACE } from "../../../theme/tokens";

type Props = {
	/** The chips — usually `MetaChip`s with `onRemove`. Nothing here when `empty`. */
	children?: ReactNode;
	/** No chips: show `placeholder` in their place, the way an empty text field shows its hint. */
	empty: boolean;
	/** What an empty field means, already translated ("Any start time"). */
	placeholder: string;
	/** Trailing control inside the field — typically a borderless "Add" button. */
	action?: ReactNode;
	/** Accessible name of the group, already translated. */
	ariaLabel: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A field-shaped row of chips: the outline, height and corners of `SystemValidationTextField`, holding a list of chosen
 * things and an "add" action, so a list control lines up with the text fields beside it instead of reading as loose
 * chips and a button. It owns only the chrome — the caller owns the chips, what adding opens, and the empty meaning.
 */
export const ChipField: FC<Props> = ({ children, empty, placeholder, action, ariaLabel, dataTestId, sx }) => (
	<Box
		role="group"
		aria-label={ariaLabel}
		data-testid={dataTestId}
		sx={[
			(theme) => ({
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				gap: 0.75,
				minHeight: CONTROL_HEIGHT.STANDARD,
				boxSizing: "border-box",
				py: 0.5,
				paddingInlineStart: 1,
				paddingInlineEnd: 0.5,
				border: `1px solid ${alpha(INK.SECONDARY, ALPHA.FIELD_OUTLINE)}`,
				borderRadius: 1,
				backgroundColor: SURFACE,
				transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				"&:focus-within": { borderColor: theme.palette.primary.main },
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}),
			...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{empty ? (
			<Typography component="span" sx={{ px: 0.5, fontSize: FONT_SIZE.SECONDARY, color: INK.DISABLED }}>
				{placeholder}
			</Typography>
		) : (
			children
		)}
		{action && <Box sx={{ marginInlineStart: "auto", display: "flex" }}>{action}</Box>}
	</Box>
);

export default ChipField;
